/**
 * Transform Movement Manager
 * 
 * Main facade for kinematic (non-physics) movement.
 * Provides all movement functionality without requiring direct access to TransformMovement class.
 */

import { TransformMovement, type MovementConfig, type MovementResult } from './Movement';
import type { Vector2 } from 'three';
import type { Object3D } from 'three';

export class TransformMovementManager {
    private movement: TransformMovement;

    /**
     * Create a Transform Movement Manager
     * @param target - Three.js Object3D to control (player, character, etc.)
     * @param config - Movement configuration (speed, sprint multiplier, jump force, gravity)
     */
    constructor(target: Object3D, config?: MovementConfig) {
        this.movement = new TransformMovement(target, config);
    }

    // ===== MOVEMENT METHODS =====

    /**
     * Apply world-space movement with smooth interpolation
     * Movement in fixed world directions (north/south/east/west)
     * @param inputVector - Input vector {x: strafe, y: forward}
     * @param isSprinting - Whether sprint is active
     * @param deltaTime - Time since last frame
     * @param lerpFactor - Movement smoothing factor (default: 0.15)
     * @returns Movement result with velocity and direction
     */
    applyWorldMovement(
        inputVector: Vector2 | { x: number; y: number },
        isSprinting: boolean = false,
        deltaTime: number = 1,
        lerpFactor: number = 0.15
    ): MovementResult {
        return this.movement.applyWorldMovement(inputVector, isSprinting, deltaTime, lerpFactor);
    }

    /**
     * Apply transform-relative movement with smooth interpolation
     * Movement relative to the object's current rotation
     * @param inputVector - Input vector {x: strafe, y: forward}
     * @param isSprinting - Whether sprint is active
     * @param deltaTime - Time since last frame
     * @param lerpFactor - Movement smoothing factor (default: 0.15)
     * @returns Movement result with velocity and direction
     */
    applyTransformRelativeMovement(
        inputVector: Vector2 | { x: number; y: number },
        isSprinting: boolean = false,
        deltaTime: number = 1,
        lerpFactor: number = 0.15
    ): MovementResult {
        return this.movement.applyTransformRelativeMovement(inputVector, isSprinting, deltaTime, lerpFactor);
    }

    /**
     * Apply camera-relative movement for third-person games
     * Movement relative to camera view with automatic character rotation
     * @param inputVector - Input vector {x: strafe, y: forward}
     * @param cameraYaw - Camera yaw angle in radians
     * @param isSprinting - Whether sprint is active
     * @param deltaTime - Time since last frame
     * @param movementLerpFactor - Movement smoothing (default: 0.15)
     * @param rotationSmoothFactor - Rotation smoothing (default: 0.15)
     * @returns Movement result with velocity and direction
     */
    applyCameraRelativeMovement(
        inputVector: Vector2 | { x: number; y: number },
        cameraYaw: number,
        isSprinting: boolean = false,
        deltaTime: number = 1,
        movementLerpFactor: number = 0.15,
        rotationSmoothFactor: number = 0.15
    ): MovementResult {
        return this.movement.applyCameraRelativeMovement(
            inputVector,
            cameraYaw,
            isSprinting,
            deltaTime,
            movementLerpFactor,
            rotationSmoothFactor
        );
    }

    // ===== GRAVITY & JUMPING =====

    /**
     * Apply gravity to the target object
     * Call this every frame to simulate falling
     * @param deltaTime - Time since last frame
     * @returns Current vertical velocity
     */
    applyGravity(deltaTime: number): number {
        return this.movement.applyGravity(deltaTime);
    }

    /**
     * Make the character jump
     * @returns True if jump was successful (only works if grounded)
     */
    jump(): boolean {
        return this.movement.jump();
    }

    /**
     * Check if character is on the ground
     * @returns True if grounded
     */
    isGrounded(): boolean {
        return this.movement.isGrounded();
    }

    /**
     * Set grounded state (usually called by collision detection)
     * @param grounded - Is character on ground?
     * @param resetVerticalVelocity - Reset vertical velocity to 0? (default: true)
     */
    setGrounded(grounded: boolean, resetVerticalVelocity: boolean = true): void {
        this.movement.setGrounded(grounded, resetVerticalVelocity);
    }

    /**
     * Get current vertical velocity
     * @returns Vertical velocity in units per second
     */
    getVerticalVelocity(): number {
        return this.movement.getVerticalVelocity();
    }

    /**
     * Reset vertical velocity to zero
     */
    resetVelocity(): void {
        this.movement.resetVelocity();
    }

    // ===== CONFIGURATION =====

    /**
     * Set movement speed
     * @param speed - Base movement speed in units per second
     */
    setSpeed(speed: number): void {
        this.movement.setSpeed(speed);
    }

    /**
     * Get current movement speed
     * @returns Base movement speed
     */
    getSpeed(): number {
        return this.movement.getSpeed();
    }

    /**
     * Set sprint multiplier
     * @param multiplier - Sprint speed multiplier (default: 2.0)
     */
    setSprintMultiplier(multiplier: number): void {
        this.movement.setSprintMultiplier(multiplier);
    }

    /**
     * Get current sprint multiplier
     * @returns Sprint multiplier
     */
    getSprintMultiplier(): number {
        return this.movement.getSprintMultiplier();
    }

    // ===== UTILITY =====

    /**
     * Get the target object being controlled
     * @returns Three.js Object3D
     */
    getTarget(): Object3D {
        return this.movement.getTarget();
    }
}

